// Static content for the rebuilt ResourcesPanel (spec 23 v2 — Resources
// Panel rebuild). Three buckets:
//
//   - SAFETY_BRIEFING     — Safety Essentials callout, responsibilities
//                            list, emergency phone numbers.
//   - EVENT_PLAN_FALLBACK — NYMR-generic Operating Pattern / Today's
//                            Programme / Visitor Flow copy used when the
//                            event's mig 052 columns are NULL.
//   - FIRST_AID           — Medical Emergency callout, defibrillator
//                            locations table, first aid kit paragraph.
//
// All three are PUBLIC CONTRACT copy (engineering lessons §10). Emergency
// numbers + defib locations are operationally critical — never paraphrase
// once shipped. Update only via a coordinated migration / data-ops PR.
//
// `mapPathForLocation` resolves `event.location` (free-text NVARCHAR(255)
// on dbo.Events) to a station image path in /public/station-maps/. The
// image set is documented in public/station-maps/manifest.json — keep
// STATION_SLUGS in sync with that file when the asset set changes.

export const SAFETY_BRIEFING = {
  intro: 'Read once at the start of each shift. Keep this open on your phone.',
  callout: {
    title: '⚠ Platform-Train Interface',
    body: 'Always stand back from the yellow line when a service is approaching or departing. Children must be held by an adult.'
  },
  responsibilities: [
    'Watch the platform edge during boarding and alighting',
    'Help wheelchair users via Carriage D and the portable ramp',
    'Report any hazard immediately to your Duty Manager',
    'In an emergency, follow the lead of the Guard and Station Foreman'
  ],
  emergencyNumbers: [
    { label: 'Control Office',          tel: '01947895468',  display: '01947 895 468' },
    { label: 'Emergency Services',      tel: '999',          display: '999' },
    { label: 'British Transport Police', tel: '08004050 40', display: '0800 40 50 40' }
  ]
} as const

export const EVENT_PLAN_FALLBACK = {
  operatingPattern:
    'Check the day’s working timetable in HOPS for confirmed services and timings. NER Autocar runs to Levisham separately from Pickering services. Any in-day changes will be announced over the platform PA.',
  todaysProgramme:
    'See the Activities tab for the live list of what’s running today. Visitor attractions vary by event — check with the Welcome team if a visitor asks about something you don’t recognise.',
  visitorFlow:
    'Standard NYMR visitor flow: arrive at car park → check in at Welcome Hut for lanyards → activities → board service → onboard experience → return → activities → depart. Watch for Carriage D priority boarding for wheelchair users.'
} as const

export const FIRST_AID = {
  callout: {
    title: '⚠ Medical Emergency',
    body: 'Call 999 first, then notify Control on 01947 895 468 and your Duty Manager.'
  },
  defibLocations: [
    { location: 'Pickering', where: 'Staff car park side of fence by footbridge (Platform 1)' },
    { location: 'Goathland', where: 'Telephone box on the Common outside the shops' },
    { location: 'Grosmont',  where: 'Wall of the signal box facing the Station Tavern' },
    { location: 'Levisham',  where: 'Tea hut' }
  ],
  firstAidKits:
    'Pickering: Signing-on Point, Tea Room, Shop. Goathland: Booking Office. Grosmont: Locomotive Workshop reception.'
} as const

// Station image set, mirrored from public/station-maps/manifest.json.
// Order matters: substring matching against `event.location` walks this
// array in order, so more-specific slugs (e.g. 'mpd' for the Motive Power
// Depot inside Grosmont) come before broader station names.
const STATION_SLUGS = [
  'pickering',
  'levisham',
  'goathland',
  'grosmont',
  'mpd',
  'whitby'
] as const
type StationSlug = typeof STATION_SLUGS[number]

const STATION_LABELS: Record<StationSlug, string> = {
  pickering: 'Pickering',
  levisham:  'Levisham',
  goathland: 'Goathland',
  grosmont:  'Grosmont',
  mpd:       'Grosmont MPD',
  whitby:    'Whitby'
}

export interface StationMapHit {
  slug: StationSlug
  label: string
  imagePath: string  // /station-maps/<slug>.jpg per manifest.json
}

// Resolves the free-text event.location to a known station map. Returns
// null when no slug substring is found — caller renders an empty state.
export function mapPathForLocation(location: string | null | undefined): StationMapHit | null {
  if (!location) return null
  const haystack = location.toLowerCase()
  // 'mpd' before 'grosmont' so a location of "Grosmont MPD" returns mpd.
  const ordered: StationSlug[] = ['mpd', 'pickering', 'levisham', 'goathland', 'grosmont', 'whitby']
  for (const slug of ordered) {
    if (haystack.includes(slug)) {
      return {
        slug,
        label: STATION_LABELS[slug],
        imagePath: `/station-maps/${slug}.jpg`
      }
    }
  }
  return null
}

// Lightweight phone-number prettifier. Recognises:
//   - UK landlines starting 01… (5-digit area + 6 digits): "01947 895468"
//   - UK landlines starting 02… (3-digit area + 8 digits): "020 7946 0958"
//   - UK mobiles  starting 07… (5+5):                       "07700 900123"
//   - 999 / 101 / 111:                                       passthrough
// Unrecognised numbers pass through unchanged so the user always gets
// SOMETHING to tap in the tel: link.
export function formatPhone(raw: string | null | undefined): string {
  if (!raw) return ''
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 0) return raw
  if (digits.length === 3) return digits                       // 999 / 101 / 111
  if (digits.length === 11 && digits.startsWith('07')) {
    return `${digits.slice(0, 5)} ${digits.slice(5)}`
  }
  if (digits.length === 11 && digits.startsWith('01')) {
    // 5-digit area code + 6-digit local (most common NYMR-area shape).
    return `${digits.slice(0, 5)} ${digits.slice(5)}`
  }
  if (digits.length === 11 && digits.startsWith('02')) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`
  }
  return raw
}
